/**
 * The one place the web client talks to the network.
 *
 * Every screen went through its own `fetch` and its own `catch`, which meant no call had a
 * deadline (a hung request hung forever, showing a spinner with no way out) and no failure
 * had a defined response — out-of-credits, an expired session and a dropped connection all
 * arrived as the same unhelpful string. Both live here now, once.
 *
 * Callers never see a status code. They get an `ApiError` with a `kind`, and each kind has
 * exactly one thing the product does about it.
 */

export type ApiErrorKind =
  | 'offline'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'out_of_credits'
  | 'not_found'
  | 'conflict'
  | 'refused'
  | 'rate_limited'
  | 'server'
  | 'misconfigured'
  | 'unknown';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;
  /**
   * The body's `error` field. Careful: the server uses it BOTH ways — a machine code on
   * some routes ('insufficient_credits') and a human sentence on others (the 422 content
   * policy) — so never branch on it without knowing which route you are reading.
   */
  readonly reason?: string;
  /**
   * The body's `reason` field: always a machine code, never prose. Branch on THIS.
   * A takedown arrives as { error: '<sentence for the user>', reason: 'taken_down' }, and
   * reading `reason` off the `error` field would mean the branch never fired.
   */
  readonly code?: string;
  /** The body's `takenDown` flag, where the server sends one. */
  readonly takenDown?: boolean;

  constructor(
    kind: ApiErrorKind,
    message: string,
    status?: number,
    reason?: string,
    extra?: { code?: string; takenDown?: boolean },
  ) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.reason = reason;
    this.code = extra?.code;
    this.takenDown = extra?.takenDown;
  }
}

export interface ApiConfig {
  /** API Gateway: data, accounts, publishing. */
  apiUrl: string;
  /** Lambda Function URL: /plan, /build, /edit, /assemble. A build outruns the 29s gateway cap. */
  buildUrl: string;
  /** Lambda Function URL: /llm. Its own origin, and its own line in the CSP's connect-src. */
  llmUrl?: string;
  getToken: () => string | null;
  fetchImpl?: typeof fetch;
}

export type Base = 'api' | 'build' | 'llm';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  base?: Base;
  /** Default 15s. A build is given far longer by its own caller. */
  timeoutMs?: number;
  /** Exposed so tests do not actually wait. */
  retryDelayMs?: number;
  /**
   * Opt a GET out of retrying. For a check whose failure the caller already handles as a
   * state — "we don't know, so act anyway" — retrying only delays the action.
   */
  retry?: boolean;
  signal?: AbortSignal;
  /**
   * Extra request headers. Added for `Idempotency-Key` on /build, which the server reads
   * before it parses the body because the claim must precede the charge.
   */
  headers?: Record<string, string>;
}

const DEFAULT_TIMEOUT_MS = 15_000;
const RETRIES = 2;

let config: ApiConfig | null = null;

export function configureApi(next: ApiConfig): void {
  config = next;
}

/** Kind, and the sentence a person should read, for one HTTP status. */
/**
 * The build service's failures that come with a sentence of their own: `{ error: <sentence>,
 * reason: <one of these> }`. Its sentence says what happened and whether anything was charged,
 * which the generic "the server had a problem" cannot.
 */
const SERVER_SENTENCE_CODES = new Set(['build_timed_out', 'model_busy', 'build_failed']);

function classify(status: number, reason?: string, code?: string): { kind: ApiErrorKind; message: string } {
  if (status === 401) return { kind: 'unauthorized', message: 'Your session has expired. Please sign in again.' };
  if (status === 402) return { kind: 'out_of_credits', message: 'You are out of credits.' };
  if (status === 403) return { kind: 'forbidden', message: 'You do not have access to that.' };
  if (status === 404) return { kind: 'not_found', message: 'We could not find that.' };
  if (status === 409) return { kind: 'conflict', message: reason ?? 'That is already taken.' };
  // 422 is the content policy: the server's sentence is the whole point, so it wins.
  if (status === 422) return { kind: 'refused', message: reason ?? 'That request was refused.' };
  if (status === 429) return { kind: 'rate_limited', message: 'Too many requests just now. Try again shortly.' };
  if (status >= 500) {
    // Still 'server', so Retry stays on offer.
    const own = reason && code && SERVER_SENTENCE_CODES.has(code) ? reason : null;
    return { kind: 'server', message: own ?? 'The server had a problem. Try again in a moment.' };
  }
  return { kind: 'unknown', message: reason ?? `That request failed (${status}).` };
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!config) throw new ApiError('misconfigured', 'The web client has no API configuration.');

  const { method = 'GET', body, base = 'api', timeoutMs = DEFAULT_TIMEOUT_MS, retryDelayMs = 300 } = options;
  const origin = (base === 'build' ? config.buildUrl : base === 'llm' ? (config.llmUrl ?? '') : config.apiUrl)
    .replace(/\/+$/, '');
  if (!origin) {
    throw new ApiError('misconfigured', 'This needs a connection and has none configured.');
  }

  const doFetch = config.fetchImpl ?? fetch;
  const url = `${origin}${path}`;
  // Only a GET may be repeated. A retried POST can publish twice, and publishing twice is
  // worse than failing once.
  const attempts = method === 'GET' && options.retry !== false ? RETRIES + 1 : 1;

  let last: ApiError | null = null;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const timer = new AbortController();
    const deadline = setTimeout(() => timer.abort(), timeoutMs);
    // The caller's own cancellation still wins.
    options.signal?.addEventListener('abort', () => timer.abort(), { once: true });

    try {
      const token = config.getToken();
      const res = await doFetch(url, {
        method,
        // The build service is a Function URL, which lowercases header names.
        headers: {
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          // Lowercased like the two above: the build service is a Function URL, which
          // lowercases header names anyway, and the server reads both spellings.
          ...Object.fromEntries(
            Object.entries(options.headers ?? {}).map(([k, v]) => [k.toLowerCase(), v]),
          ),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        signal: timer.signal,
      });

      if (res.ok) {
        if (res.status === 204) return undefined as T;
        const text = await res.text();
        if (!text) return undefined as T;
        try {
          return JSON.parse(text) as T;
        } catch {
          throw new ApiError('server', 'The server sent a reply we could not read.', res.status);
        }
      }

      // A reason, if there is one. A gateway error page is not JSON and that is fine.
      let reason: string | undefined;
      let code: string | undefined;
      let takenDown: boolean | undefined;
      try {
        const parsed = JSON.parse(await res.text()) as {
          error?: unknown;
          reason?: unknown;
          takenDown?: unknown;
        };
        if (typeof parsed.error === 'string') reason = parsed.error;
        if (typeof parsed.reason === 'string') code = parsed.reason;
        if (typeof parsed.takenDown === 'boolean') takenDown = parsed.takenDown;
      } catch {
        /* not JSON */
      }

      const { kind, message } = classify(res.status, reason, code);
      last = new ApiError(kind, message, res.status, reason, { code, takenDown });
      // A 4xx will fail the same way however many times we ask.
      if (res.status < 500) throw last;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status !== undefined && err.status < 500) throw err;
        last = err;
        // An aborted fetch throws a DOMException, which is NOT an instanceof Error in a
        // browser — checking for one reported every timeout as "offline".
      } else if ((err as { name?: unknown } | null)?.name === 'AbortError') {
        // Distinguishing these matters: one is the caller's choice, one is our deadline.
        last = options.signal?.aborted
          ? new ApiError('unknown', 'That request was cancelled.')
          : new ApiError('timeout', 'That took too long. Try again.');
        if (options.signal?.aborted) throw last;
      } else {
        last = new ApiError('offline', 'We could not reach the server. Check your connection.');
      }
    } finally {
      clearTimeout(deadline);
    }

    if (attempt < attempts - 1) {
      // Backoff with jitter, so a server coming back up is not hit by everyone at once.
      await sleep(retryDelayMs * 2 ** attempt + Math.random() * retryDelayMs);
    }
  }

  throw last ?? new ApiError('unknown', 'That request failed.');
}
